import { useQuery, UseQueryResult } from "react-query";
import CustomerApiService from "../../../CustomerApiService";
import { ConditionalRule, MappingMode } from "../mapping/service";

/**
 * `spotter` unified field tab API calls — merges the Field Mapping and Glossary tabs into one
 * screen. Mirrors `../mapping/service.ts`'s convention: thin wrappers over
 * `CustomerApiService.fetch`, bare-JSON contract, one function per endpoint. Types here mirror
 * `apps/customer-api/src/routes/spotter/fields.ts`'s `UnifiedFieldEntry` union verbatim (see that
 * file's doc comment for the byte-compatibility story with the legacy `GET /mapping`/
 * `GET /glossary/terms` routes this replaces for the frontend).
 *
 * Facial Recognition person-routing lives entirely under `../frMapping/service.ts`'s own
 * `GET`/`PUT /fr-mapping` — this route neither reads nor writes it.
 */

const PREFIX = "/spotter";

export type UnifiedFieldType = "mapped" | "glossary";

export interface MappedFieldEntry {
  type: "mapped";
  /** Set to `"comment"` for the always-available "map to an iconik comment" destination (Seth
   *  Tanner, Monday 12926430722 — see
   *  `apps/customer-api/src/services/customerSpotterMapping.ts`'s module doc comment). Absent
   *  for an ordinary view/field destination, the only case where
   *  `to_view_id`/`to_field_id` below are present. */
  destination?: "comment";
  to_view_id?: string;
  to_field_id?: string;
  scope: "asset" | "segment";
  from: string | string[];
  mode: MappingMode;
  value_map?: Record<string, string>;
  template?: string;
  merge?: "append_unique";
  /** `mode === "conditional"` only — see `ConditionalRule`'s own doc comment. */
  rules?: ConditionalRule[];
  /** Option/tag values on this destination field that should never be written, regardless of
   *  what the source(s) would otherwise produce (Seth Tanner's "do not map this tag" ask). Only
   *  rendered in the editor when the destination field is itself one of the org's eligible
   *  picklist fields (see `getEligibleFields` below) — a plain text destination has no option
   *  list to exclude values from. */
  excludedTags?: string[];
}

export interface GlossaryFieldEntry {
  type: "glossary";
  to_view_id: string;
  to_field_id: string;
  fieldLabel: string;
  terms: Record<string, string>;
  excludedTags?: string[];
}

export type UnifiedFieldEntry = MappedFieldEntry | GlossaryFieldEntry;

/** `GET /fields` response — `contract_version` is `null` when nothing has been saved yet. */
export interface StoredFields {
  contract_version: string | null;
  fields: UnifiedFieldEntry[];
}

/** GET `/fields` — every currently configured field, of every type, in one call. */
export async function getFields(): Promise<StoredFields> {
  return CustomerApiService.fetch<StoredFields>("GET", `${PREFIX}/fields`);
}

/** PUT `/fields` — persist the full current field list. Full replace-by-diff at the storage layer
 *  (see the backend route's doc comment) — the caller always submits every currently configured
 *  field, same convention `../mapping/service.ts`'s `saveMapping` already uses. */
export async function saveFields(body: {
  contract_version: string;
  fields: UnifiedFieldEntry[];
}): Promise<StoredFields> {
  return CustomerApiService.fetch<StoredFields>(
    "PUT",
    `${PREFIX}/fields`,
    body
  );
}

export const FIELDS_QUERY_KEY = "spotter-fields";

export function useFields(): UseQueryResult<StoredFields, Error> {
  return useQuery<StoredFields, Error>(FIELDS_QUERY_KEY, getFields);
}

/** One field eligible for Glossary OR Facial Recognition — a real, org-configured iconik picklist
 *  field (drop_down/tag_cloud with non-empty `options`) on the asset view, live from iconik. This
 *  hits the SAME `GET /glossary/fields` endpoint the old Glossary tab used — per the unification
 *  work order, Facial Recognition reuses that eligibility check rather than getting its own. */
export interface EligibleFieldOption {
  name: string;
  label: string;
  terms: string[];
}

export async function getEligibleFields(): Promise<EligibleFieldOption[]> {
  return CustomerApiService.fetch<EligibleFieldOption[]>(
    "GET",
    `${PREFIX}/glossary/fields`
  );
}
